import React, { useEffect, useMemo, useState } from "react";
import { Copy, Plus, Printer, Trash2 } from "lucide-react";
import { Modal } from "./common";
import { useLang } from "../i18n";
import api, { apiError } from "../api";
import { toast } from "sonner";

const emptyRow = () => ({
  id: crypto.randomUUID(),
  name: "",
  username: "",
  class_id: "",
});

export default function BulkAddStudentsModal({ open, onClose, onDone, classes = [] }) {
  const { t, pick } = useLang();
  const [rows, setRows] = useState([emptyRow(), emptyRow()]);
  const [sharedClassId, setSharedClassId] = useState("");
  const [createLogin, setCreateLogin] = useState(false);
  const [errors, setErrors] = useState({});
  const [busy, setBusy] = useState(false);
  const [results, setResults] = useState(null);

  useEffect(() => {
    if (open) {
      setRows([emptyRow(), emptyRow()]);
      setSharedClassId("");
      setCreateLogin(false);
      setErrors({});
      setResults(null);
    }
  }, [open]);

  const className = (classId) => {
    const currentClass = classes.find((item) => item.id === classId);
    return currentClass ? pick(currentClass, "name") : "—";
  };

  const updateRow = (id, key, value) => {
    setRows((current) => current.map((row) => (
      row.id === id ? { ...row, [key]: value } : row
    )));
    setErrors((current) => ({ ...current, [id]: { ...current[id], [key]: null } }));
  };

  const addRow = () => setRows((current) => [...current, emptyRow()]);

  const removeRow = (id) => {
    if (rows.length === 1) return;
    setRows((current) => current.filter((row) => row.id !== id));
  };

  const validate = () => {
    const nextErrors = {};
    const usernames = new Map();
    rows.forEach((row) => {
      const name = row.name.trim();
      const username = row.username.trim().toLowerCase();
      if (!name) {
        nextErrors[row.id] = { ...nextErrors[row.id], name: t("validation.required") };
      }
      if (createLogin && !username) {
        nextErrors[row.id] = { ...nextErrors[row.id], username: t("validation.required") };
      } else if (createLogin && !/^[a-zA-Z0-9_.@-]{3,50}$/.test(username)) {
        nextErrors[row.id] = {
          ...nextErrors[row.id],
          username: t("validation.username_invalid"),
        };
      } else if (createLogin && usernames.has(username)) {
        const firstId = usernames.get(username);
        nextErrors[row.id] = {
          ...nextErrors[row.id],
          username: t("teacher.bulk_username_duplicate"),
        };
        nextErrors[firstId] = {
          ...nextErrors[firstId],
          username: t("teacher.bulk_username_duplicate"),
        };
      } else if (createLogin) {
        usernames.set(username, row.id);
      }
    });
    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const showServerErrors = (serverErrors) => {
    const nextErrors = {};
    serverErrors.forEach(({ index, field, code }) => {
      const row = rows[index];
      if (!row) return;
      const key = field === "name" ? "name" : "username";
      const message = code === "username_duplicate"
        ? t("teacher.bulk_username_duplicate")
        : t(`validation.${code}`);
      nextErrors[row.id] = { ...nextErrors[row.id], [key]: message };
    });
    setErrors(nextErrors);
  };

  const submit = async () => {
    if (!validate()) return;
    setBusy(true);
    try {
      const { data } = await api.post("/staff/students/bulk", {
        class_id: sharedClassId || null,
        create_login: createLogin,
        auto_generate_usernames: createLogin,
        students: rows.map((row) => ({
          name_ar: row.name.trim(),
          username: row.username.trim().toLowerCase(),
          class_id: row.class_id || null,
        })),
      });
      setResults(data.results);
      toast.success(t("teacher.bulk_students_created", { n: data.results.length }));
      onDone?.();
    } catch (error) {
      const serverErrors = error.response?.data?.detail?.errors;
      if (Array.isArray(serverErrors)) {
        showServerErrors(serverErrors);
        toast.error(t("teacher.bulk_fix_errors"));
      } else {
        toast.error(apiError(error, t));
      }
    } finally {
      setBusy(false);
    }
  };

  const credentials = useMemo(
    () => (results || []).filter((student) => student.login_enabled).map((student) => [
      student.name_ar,
      `${student.username} / ${student.temp_password}`,
      className(student.class_id),
    ].join("\n")).join("\n\n"),
    [results, classes]
  );

  const copyAll = async () => {
    try {
      await navigator.clipboard.writeText(credentials);
      toast.success(t("teacher.bulk_credentials_copied"));
    } catch {
      toast.error(t("common.error_generic"));
    }
  };

  const close = () => {
    if (!busy) onClose();
  };

  return (
    <Modal open={open} onClose={close} title={t("teacher.add_multiple_students")}
      testId="bulk-add-students-modal" wide>
      {results ? (
        <div className="space-y-4">
          <p data-testid="bulk-created-summary" className="text-lg font-extrabold text-[#48D276]">
            {t("teacher.bulk_students_created", { n: results.length })}
          </p>
          <div className="overflow-x-auto rounded-2xl border border-[#FFC83D]/30">
            <table data-testid="bulk-credentials-table" className="w-full text-sm" dir="auto">
              <thead className="bg-[#0D0B38]/70 text-[#A5A0D6]">
                <tr>
                  <th className="p-3 text-start">{t("common.name")}</th>
                  {createLogin && <th className="p-3 text-start">{t("teacher.username")}</th>}
                  {createLogin && <th className="p-3 text-start">{t("teacher.temp_password")}</th>}
                  <th className="p-3 text-start">{t("teacher.class_label")}</th>
                </tr>
              </thead>
              <tbody>
                {results.map((student) => (
                  <tr key={student.id} data-testid={`bulk-credential-${student.username}`}
                    className="border-t border-white/10">
                    <td className="p-3 font-bold">{pick(student, "name")}</td>
                    {createLogin && <td className="p-3 font-bold" dir="ltr">{student.username}</td>}
                    {createLogin && (
                      <td className="p-3 font-black text-[#FFC83D]" dir="ltr">
                        {student.temp_password}
                      </td>
                    )}
                    <td className="p-3">{className(student.class_id)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {createLogin && (
            <div className="flex flex-wrap gap-3">
              <button data-testid="copy-all-credentials-btn" onClick={copyAll}
                className="btn-magic flex-1">
                <Copy className="w-4 h-4" /> {t("teacher.copy_all_credentials")}
              </button>
              <button data-testid="print-bulk-credentials-btn" onClick={() => window.print()}
                className="btn-magic btn-ghost">
                <Printer className="w-4 h-4" /> {t("teacher.print_credentials")}
              </button>
            </div>
          )}
          <button data-testid="bulk-add-students-done-btn" onClick={close}
            className="btn-magic w-full">
            {t("common.close")}
          </button>
        </div>
      ) : (
        <div className="space-y-4">
          <div>
            <label className="text-sm font-extrabold block mb-1">
              {t("teacher.bulk_shared_class")}
            </label>
            <select data-testid="bulk-shared-class-select" className="input-magic"
              value={sharedClassId}
              onChange={(event) => setSharedClassId(event.target.value)}>
              <option value="" label={t("teacher.bulk_choose_per_row")} />
              {classes.map((item) => (
                <option key={item.id} value={item.id} label={pick(item, "name")} />
              ))}
            </select>
          </div>
          <label data-testid="bulk-create-login-toggle"
            className="flex items-center gap-2 text-sm font-extrabold">
            <input data-testid="bulk-create-login-checkbox" type="checkbox" checked={createLogin}
              onChange={(event) => setCreateLogin(event.target.checked)} />
            {t("teacher.create_login_now")}
          </label>
          <div className="space-y-3">
            {rows.map((row, index) => (
              <div key={row.id} data-testid={`bulk-student-row-${index}`}
                className={`rounded-2xl border bg-[#0D0B38]/45 p-3 ${errors[row.id]
                  ? "border-[#F43F5E]/70"
                  : "border-white/10"}`}>
                <div className="flex items-center justify-between gap-3 mb-3">
                  <p className="font-extrabold text-sm">
                    {t("teacher.bulk_student_number", { n: index + 1 })}
                  </p>
                  <button data-testid={`remove-bulk-student-${index}`}
                    onClick={() => removeRow(row.id)}
                    disabled={rows.length === 1} title={t("teacher.bulk_remove_row")}
                    className="w-8 h-8 rounded-lg text-[#FF8FB1] hover:bg-[#F43F5E]/15
                      disabled:opacity-30">
                    <Trash2 className="w-4 h-4 mx-auto" />
                  </button>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <div>
                    <label className="text-xs font-bold text-[#D8D4F2] block mb-1">
                      {t("teacher.bulk_student_name")} *
                    </label>
                    <input data-testid={`bulk-student-name-${index}`} className="input-magic"
                      value={row.name}
                      onChange={(event) => updateRow(row.id, "name", event.target.value)} />
                    {errors[row.id]?.name && (
                      <p data-testid={`bulk-student-name-error-${index}`}
                        className="text-xs text-[#FF8FB1] mt-1">
                        {errors[row.id].name}
                      </p>
                    )}
                  </div>
                  {createLogin && (
                    <div>
                    <label className="text-xs font-bold text-[#D8D4F2] block mb-1">
                      {t("teacher.username_optional")}
                    </label>
                    <input data-testid={`bulk-student-username-${index}`} className="input-magic"
                      dir="ltr"
                      value={row.username}
                      onChange={(event) => updateRow(row.id, "username", event.target.value)} />
                    {errors[row.id]?.username && (
                      <p data-testid={`bulk-student-username-error-${index}`}
                        className="text-xs text-[#FF8FB1] mt-1">
                        {errors[row.id].username}
                      </p>
                    )}
                    </div>
                  )}
                  <div>
                    <label className="text-xs font-bold text-[#D8D4F2] block mb-1">
                      {t("teacher.class_label")}
                    </label>
                    <select data-testid={`bulk-student-class-${index}`} className="input-magic"
                      disabled={Boolean(sharedClassId)} value={sharedClassId || row.class_id}
                      onChange={(event) => updateRow(row.id, "class_id", event.target.value)}>
                      <option value="" label="—" />
                      {classes.map((item) => (
                        <option key={item.id} value={item.id} label={pick(item, "name")} />
                      ))}
                    </select>
                  </div>
                </div>
              </div>
            ))}
          </div>
          <button data-testid="add-bulk-student-row-btn" onClick={addRow}
            className="btn-magic btn-ghost w-full">
            <Plus className="w-4 h-4" /> {t("teacher.bulk_add_row")}
          </button>
          <button data-testid="bulk-add-students-submit" disabled={busy} onClick={submit}
            className="btn-magic w-full">
            {busy ? t("common.loading") : t("teacher.add_multiple_students")}
          </button>
        </div>
      )}
    </Modal>
  );
}